import type { Me, Rule } from "@riea-water/contracts";
import { useRef, useState, type FormEvent } from "react";
import { useOutletContext } from "react-router";
import { useRules, useUpdateRule } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { useToast } from "../shell/toast.js";
import { SEVERITY_TONE, shortDate } from "./findings-helpers.js";
import { number } from "./imports-helpers.js";

const CAN_EDIT: readonly Me["role"][] = ["tenant_admin", "data_lead"];

/** The owner column as the prototype words it. */
export function ownerLabel(ownerParty: string): string {
  switch (ownerParty) {
    case "wholesaler":
      return "Wholesaler";
    case "retailer":
      return "Retailer";
    case "derive":
      return "Submitter";
    case "sender":
      return "Sender";
    default:
      return ownerParty;
  }
}

/** The parameters a person can change in the dialog: scalars; tables stay in the pack. */
function editableParams(rule: Rule): [string, string | number | boolean][] {
  return Object.entries(rule.params).filter(
    (entry): entry is [string, string | number | boolean] =>
      typeof entry[1] === "string" || typeof entry[1] === "number" || typeof entry[1] === "boolean",
  );
}

/** Setup · Rules (RUL-03, ADM-03, AC-2-08): the table and the Edit sheet that needs a reason and applies from the next run. */
export function Rules() {
  const { client, me } = useOutletContext<ShellContext>();
  const rules = useRules(client);
  const update = useUpdateRule(client);
  const { toast, Toast } = useToast();
  const dialog = useRef<HTMLDialogElement>(null);
  const [editing, setEditing] = useState<Rule | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    const form = new FormData(event.currentTarget);
    const text = (name: string) => {
      const value = form.get(name);
      return typeof value === "string" ? value : "";
    };
    const params: Record<string, unknown> = {};
    for (const [key, current] of editableParams(editing)) {
      const raw = text(`param:${key}`);
      if (typeof current === "number") {
        const n = Number(raw);
        if (raw !== "" && Number.isFinite(n) && n !== current) params[key] = n;
      } else if (typeof current === "boolean") {
        const b = form.get(`param:${key}`) === "on";
        if (b !== current) params[key] = b;
      } else if (raw !== current) params[key] = raw;
    }
    const severity = text("severity") as Rule["severity"];
    const enabled = form.get("enabled") === "on";
    update.mutate(
      {
        ruleId: editing.id,
        ...(Object.keys(params).length ? { params } : {}),
        ...(severity !== editing.severity ? { severity } : {}),
        ...(enabled !== editing.enabled ? { enabled } : {}),
        reason: text("reason"),
      },
      {
        onSuccess: (rule) => {
          dialog.current?.close();
          setEditing(null);
          toast(`${rule.id} updated · RuleConfigChanged recorded · applies from the next run`);
        },
        onError: (error) => toast(`Change refused · ${error.message}`),
      },
    );
  };

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Setup</div>
          <h1>Rules</h1>
        </div>
        <span className="muted" style={{ fontSize: ".8rem" }}>
          Thresholds are working assumptions until a pilot calibrates them
        </span>
      </div>
      <section className="card">
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>Rule</th>
                <th>Severity</th>
                <th>Owner</th>
                <th>Threshold</th>
                <th>Market measure</th>
                <th className="num">Open</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.isPending ? (
                [0, 1, 2].map((i) => (
                  <tr key={i}>
                    <td colSpan={7}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))
              ) : rules.isError ? (
                <tr>
                  <td colSpan={7} className="empty">
                    The rules could not be loaded: {rules.error.message}. Try again in a moment.
                  </td>
                </tr>
              ) : (
                (rules.data ?? []).map((rule) => (
                  <tr key={rule.id} style={rule.enabled ? undefined : { opacity: 0.6 }}>
                    <td>
                      <b>{rule.name}</b>{" "}
                      <span className="mono faint" style={{ fontSize: ".7rem" }}>
                        {rule.id}
                      </span>
                      {rule.enabled ? null : (
                        <>
                          {" "}
                          <span className="pill off">off</span>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${SEVERITY_TONE[rule.severity]}`}>
                        {rule.severity}
                      </span>
                    </td>
                    <td>{ownerLabel(rule.ownerParty)}</td>
                    <td className="mono" style={{ fontSize: ".76rem" }}>
                      {rule.threshold}
                      {rule.overridden && rule.changedAt ? (
                        <>
                          <br />
                          <span
                            className="faint"
                            style={{ fontFamily: "var(--f-body)", fontSize: ".72rem" }}
                          >
                            changed by {rule.changedBy ?? "someone"} on {shortDate(rule.changedAt)}
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {rule.mpfRelevant ? (
                        <span className="pill info">relevant</span>
                      ) : (
                        <span className="faint">no</span>
                      )}
                    </td>
                    <td className="num">{number(rule.openCount)}</td>
                    <td>
                      {CAN_EDIT.includes(me.role) ? (
                        <button
                          className="btn sm"
                          type="button"
                          onClick={() => {
                            setEditing(rule);
                            dialog.current?.showModal();
                          }}
                        >
                          Edit
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <dialog className="sheet" ref={dialog} aria-labelledby="rule-edit-title">
        {editing ? (
          <form method="dialog" onSubmit={onSubmit} key={editing.id}>
            <h2 id="rule-edit-title" style={{ margin: 0 }}>
              Edit {editing.id} · {editing.name}
            </h2>
            {editableParams(editing).map(([key, value]) =>
              typeof value === "boolean" ? (
                <label key={key} style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
                  <input type="checkbox" name={`param:${key}`} defaultChecked={value} />
                  {key.replace(/_/g, " ")}
                </label>
              ) : (
                <label key={key}>
                  {key.replace(/_/g, " ")}
                  <input
                    name={`param:${key}`}
                    type={typeof value === "number" ? "number" : "text"}
                    step="any"
                    defaultValue={String(value)}
                  />
                </label>
              ),
            )}
            <label>
              Severity
              <select name="severity" defaultValue={editing.severity}>
                <option value="critical">critical</option>
                <option value="high">high</option>
                <option value="medium">medium</option>
                <option value="low">low</option>
              </select>
            </label>
            <label style={{ gridTemplateColumns: "auto 1fr", alignItems: "center" }}>
              <input type="checkbox" name="enabled" defaultChecked={editing.enabled} />
              Enabled
            </label>
            <label>
              Reason for the change
              <textarea name="reason" required minLength={5} rows={3} />
            </label>
            <div className="faint" style={{ fontSize: ".76rem" }}>
              Threshold changes need a reason and apply from the next run. The change is recorded as
              RuleConfigChanged.
            </div>
            <div className="split" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn sm"
                type="button"
                onClick={() => {
                  dialog.current?.close();
                  setEditing(null);
                }}
              >
                Cancel
              </button>
              <button className="btn sm primary" type="submit" disabled={update.isPending}>
                Record the change
              </button>
            </div>
          </form>
        ) : null}
      </dialog>
      <Toast />
    </>
  );
}
